import React, { useState, useEffect } from "react";
import axiosClient from "../../axios";

const OrdersDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch orders from API
  const fetchOrders = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosClient.get("/api/orders");

      if (response.data.success && response.data.orders) {
        setOrders(response.data.orders);
        setFilteredOrders(response.data.orders);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to load orders"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, []);

  // Get unique months from orders
  const getAvailableMonths = () => {
    const months = orders
      .filter((order) => order.paid_at)
      .map((order) => {
        const date = new Date(order.paid_at);
        return {
          value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}`,
          label: date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
          }),
        };
      });

    const uniqueMonths = Array.from(
      new Map(months.map((item) => [item.value, item])).values()
    );

    return uniqueMonths.sort((a, b) => b.value.localeCompare(a.value));
  };

  // Filter orders based on selected month and search term
  useEffect(() => {
    let filtered = [...orders];

    // Filter by month
    if (selectedMonth !== "all") {
      filtered = filtered.filter((order) => {
        if (!order.paid_at) return false;
        const orderDate = new Date(order.paid_at);
        const orderMonth = `${orderDate.getFullYear()}-${String(
          orderDate.getMonth() + 1
        ).padStart(2, "0")}`;
        return orderMonth === selectedMonth;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.order_id.toLowerCase().includes(searchLower) ||
          order.user_name.toLowerCase().includes(searchLower) ||
          (order.user_email &&
            order.user_email.toLowerCase().includes(searchLower)) ||
          (order.razorpay_payment_id &&
            order.razorpay_payment_id.toLowerCase().includes(searchLower)) ||
          (order.razorpay_order_id &&
            order.razorpay_order_id.toLowerCase().includes(searchLower))
      );
    }

    setFilteredOrders(filtered);
  }, [selectedMonth, searchTerm, orders]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };

  const exportToCSV = () => {
    const headers = [
      "Order ID",
      "Customer",
      "Email",
      "Amount",
      "Discount",
      "Tax",
      "Status",
      "Coupon",
      "Payment ID",
      "Date",
    ];
    const rows = filteredOrders.map((order) => [
      order.order_id,
      order.user_name,
      order.user_email || "N/A",
      order.amount,
      order.discount_amount,
      order.tax_amount,
      order.status,
      order.coupon_code || "N/A",
      order.razorpay_payment_id || "N/A",
      order.paid_at ? new Date(order.paid_at).toISOString() : "N/A",
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${selectedMonth}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = {
    total: filteredOrders.length,
    paid: filteredOrders.filter((o) => o.status === "paid").length,
    pending: filteredOrders.filter((o) => o.status === "pending").length,
    revenue: filteredOrders
      .filter((o) => o.status === "paid")
      .reduce((sum, o) => sum + o.amount, 0),
  };

  if (isLoading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <svg
            className="bi flex-shrink-0 me-2"
            width="24"
            height="24"
            role="img"
            aria-label="Danger:"
          >
            <use xlinkHref="#exclamation-triangle-fill" />
          </svg>
          <div>
            <strong>Error Loading Orders:</strong> {error}
            <button
              onClick={fetchOrders}
              className="btn btn-sm btn-outline-danger ms-3"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Orders Dashboard</h2>
          <p className="text-muted">Manage and track all your orders</p>
        </div>
        <button
          onClick={fetchOrders}
          disabled={isLoading}
          className="btn btn-outline-primary"
        >
          <i
            className={`bi bi-arrow-clockwise ${
              isLoading ? "spinner-border spinner-border-sm" : ""
            }`}
          ></i>{" "}
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Total Orders</h6>
              <h3 className="mb-0">{stats.total}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Paid Orders</h6>
              <h3 className="mb-0 text-success">{stats.paid}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Pending Orders</h6>
              <h3 className="mb-0 text-warning">{stats.pending}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-2">Total Revenue</h6>
              <h3 className="mb-0 text-primary">
                {formatCurrency(stats.revenue)}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Search */}
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Month Filter */}
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-calendar"></i>
                </span>
                <select
                  className="form-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="all">All Months</option>
                  {getAvailableMonths().map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Export Button */}
            <div className="col-md-4">
              <button
                onClick={exportToCSV}
                disabled={filteredOrders.length === 0}
                className="btn btn-success w-100"
              >
                <i className="bi bi-download"></i> Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-4 py-3">Order ID</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Tax</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <i className="bi bi-inbox display-4 text-muted"></i>
                      <p className="mt-3 text-muted">No orders found</p>
                      <small className="text-muted">
                        Try adjusting your filters or search term
                      </small>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-4 py-3">
                        <div className="fw-semibold">{order.order_id}</div>
                        {order.coupon_code && (
                          <div className="small text-primary mt-1">
                            🎫 {order.coupon_code}
                          </div>
                        )}
                        {order.razorpay_order_id && (
                          <div className="small text-muted mt-1">
                            {order.razorpay_order_id.substring(0, 20)}...
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="fw-medium">{order.user_name}</div>
                        {order.user_email && (
                          <div className="small text-muted">
                            {order.user_email}
                          </div>
                        )}
                        {order.razorpay_payment_id && (
                          <div className="small text-muted mt-1">
                            {order.razorpay_payment_id}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="fw-bold">
                          {formatCurrency(order.amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {order.discount_amount > 0 ? (
                          <span className="text-success fw-medium">
                            -{formatCurrency(order.discount_amount)}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span>{formatCurrency(order.tax_amount)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`badge bg-${getStatusBadge(order.status)}`}
                        >
                          {order.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-muted small">
                          {formatDate(order.paid_at || order.created_at)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Footer */}
      {filteredOrders.length > 0 && (
        <div className="card shadow-sm mt-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">
                Showing <strong>{filteredOrders.length}</strong> of{" "}
                <strong>{orders.length}</strong> orders
              </span>
              <span className="text-muted">
                Total Revenue (filtered):{" "}
                <strong className="text-dark">
                  {formatCurrency(stats.revenue)}
                </strong>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersDashboard;
